import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQueryWithReauth';

// Define a service using a base URL and expected endpoints
export const boreholeApi = createApi({
  reducerPath: 'boreholeApi',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: 0,
  keepUnusedDataFor: 0,
  tagTypes: ['borehole'],
  endpoints: (builder) => ({
    getBorehole: builder.query<any, { clientId: string, projectId: string, boreholeId: string }>({      
      query: (arg) => {
        const { boreholeId, clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole/${boreholeId}`
      },
      providesTags: (returnValue, args) => [{ type: 'borehole' as const, id: 'LIST' }, ...(returnValue?.data || []).map((borehole: any) => ({ type: 'borehole' as const, id: borehole.id }))]
      //['borehole']
    }),
    getClientLogo: builder.query<any, { clientId: string }>({      
      query: (arg) => {
        const { clientId } = arg;
        return `client/${clientId}/Logo`
      }
    }),
    getBoreholeSummary: builder.query<any, { clientId: string, projectId: string, boreholeId: string }>({      
      query: (arg) => {
        const { boreholeId, clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole/${boreholeId}/summary`
      },
      providesTags: (returnValue, args) => [{ type: 'borehole' as const, id: 'LIST' }, ...(returnValue?.data || []).map((borehole: any) => ({ type: 'borehole' as const, id: borehole.id }))]
      //['borehole']
    }),
    getBoreholeFiles: builder.query<any, { clientId: string, projectId: string, boreholeId: string }>({      
      query: (arg) => {
        const { boreholeId, clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole/${boreholeId}/files`
      },
      providesTags: (returnValue, args) => [{ type: 'borehole' as const, id: 'LIST' }, ...(returnValue?.data || []).map((borehole: any) => ({ type: 'borehole' as const, id: borehole.id }))]
      //['borehole']
    }),
    getBoreholeRawFiles: builder.query<any, { clientId: string, projectId: string, boreholeId: string }>({      
      query: (arg) => {
        const { boreholeId, clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole/${boreholeId}/download/list`
      },
      //providesTags: (returnValue, args) => [{ type: 'borehole' as const, id: 'LIST' }, ...(returnValue?.data || []).map((borehole: any) => ({ type: 'borehole' as const, id: borehole.id }))]
      //['borehole']
    }),
    getBoreholes: builder.query<any, { clientId: string, sortColumn: string, sortAscending: boolean, continuationToken: string | null, nextPage: number | null, pageSize: number}>({
      query: (arg) => { 
        const { clientId, sortColumn, sortAscending, continuationToken, nextPage, pageSize } = arg;
        let continuationTokenQuery = "";
        if (nextPage) {
          continuationTokenQuery = `&page=${nextPage}`;
        }
        return `client/${clientId}/borehole/${sortColumn}/${sortAscending}?pageSize=${pageSize}${continuationTokenQuery}` },
    }),
    getProjectBoreholes: builder.query<any, { clientId: string, projectId: string}>({
      query: (arg) => { 
        const { clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole` },
    }),
    getBoreholeLocations: builder.query<any, { clientId: string, projectId: string}>({
      query: (arg) => { 
        const { clientId, projectId } = arg;
        return `client/${clientId}/project/${projectId}/borehole/locations` },
    }),
    getCrimeLocations: builder.query<any, void>({
      query: (arg) => {     
        return `https://data.police.uk/api/crimes-street/all-crime?lat=52.2648245&lng=-1.1640192&date=2019-10` },
    }),
    uploadFile: builder.mutation<any, FormData>({
      query: (formData: FormData) => ({
        url: 'upload',
        method: 'POST',
        headers: {},
        body: formData,
      }),
      invalidatesTags: ['borehole'],
    }),
    uploadImages: builder.mutation<any, { clientId: string, projectId: string, boreholeId: string }>({
      query: (arg: any) => {
        const { boreholeId, clientId, projectId } = arg;
        return {
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/Upload`,
        method: 'POST',
        headers: {}
      }},
      invalidatesTags: ['borehole'],
    }),
    scaleImages: builder.mutation<any, { clientId: string, projectId: string, boreholeId: string, imageType?: number | null, scaleLogViewer: boolean, scalePdf: boolean }>({
      query: (arg) => {
        const { boreholeId, clientId, projectId, imageType, scaleLogViewer, scalePdf } = arg;
        return {        
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/scale`,
        method: 'POST',
        headers: {},
        body: {
          imageType, scaleLogViewer, scalePdf
        }
      }},
      //invalidatesTags: ['borehole'],
    }),  

    convertImagesToPng: builder.mutation<any, { clientId: number, projectId: number, boreholeId: string | null }>({
      query: (arg) => {
        const { boreholeId, clientId, projectId } = arg;
        return {        
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/ConvertImagesToPng`,
        method: 'POST',
        headers: {},
        body: {
          boreholeIds: [boreholeId]
        }
      }},
      //invalidatesTags: ['borehole'],
    }),  
    scaleData: builder.mutation<any, { id: number, clientId: string, projectId: string, boreholeId: string, uploadType: string, fileName: string }>({
      query: (arg) => {
        const { id, boreholeId, clientId, projectId, uploadType, fileName } = arg;
        return {        
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/trackData/queueScale`,
        method: 'POST',
        headers: {},
        body: {
          boreholeDataFileId: id,
          uploadType,
          fileName
        }
      }},
      //invalidatesTags: ['borehole'],
    }), 
    createDeepZoomImages: builder.mutation<any, { clientId: string, projectId: string, boreholeId: string, imageType?: number | null }>({
      query: (arg) => {
        const { boreholeId, clientId, projectId, imageType } = arg;
        return {        
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/CreateDeepZoom`,
        method: 'POST',
        headers: {},
        body: {
          imageType
        }
      }},
      //invalidatesTags: ['borehole'],
    }),  
    add: builder.mutation<any, any>({
      query: (formData: any) => {
        const { clientId, projectId } = formData;
        return {
        url: `client/${clientId}/project/${projectId}/borehole`,
        method: 'POST',
        body: formData,
      }},
      invalidatesTags: ['borehole'],
    }),  
    update: builder.mutation<any, { borehole : any, file: any }>({
      query: (arg: any) => {
        const { clientId, projectId, boreholeId } = arg.borehole;
        const formPayload = new FormData();        
        formPayload.append('model', JSON.stringify(arg.borehole))
        formPayload.append('file', arg.file);
        return {
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}`,
        method: 'PUT',
        body: formPayload,
      }},
      invalidatesTags: ['borehole'],
    }),
    delete: builder.mutation<any, any>({
      query: (formData: any) => {
        const { clientId, projectId, boreholeId } = formData;
        return {
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}`,
        method: 'DELETE'
      }},
      invalidatesTags: ['borehole'],
    }),
    downloadFiles: builder.mutation<any, { clientId: string, projectId: string, boreholeId: string, fileNames: string[] }>({
      query: (arg: any) => {
        const { boreholeId, clientId, projectId, fileNames } = arg;
        return {
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/download`,
        method: 'POST',
        headers: {},
        body: fileNames,
      }},
      invalidatesTags: ['borehole'],
    }),
    exportFile: builder.mutation<any, any>({
      query: (formData: any) => {        
        const { clientId, projectId, boreholeId } = formData;
        return {
        url: `client/${clientId}/project/${projectId}/borehole/${boreholeId}/export`,
        method: 'POST',
        headers: {},
        body: formData,
      }}      
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetBoreholeQuery, 
               useGetClientLogoQuery,
               useGetBoreholeSummaryQuery, 
               useGetBoreholeFilesQuery, 
               useGetBoreholeRawFilesQuery, 
               useGetBoreholesQuery, 
               useGetBoreholeLocationsQuery,
               useGetCrimeLocationsQuery,
               useUploadFileMutation, 
               useAddMutation, 
               useUpdateMutation, 
               useDeleteMutation, 
               useUploadImagesMutation, 
               useScaleImagesMutation,
               useScaleDataMutation,
               useDownloadFilesMutation,
               useCreateDeepZoomImagesMutation,
               useExportFileMutation, 
               useConvertImagesToPngMutation } = boreholeApi;